import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, User, UserPlus, Check, RefreshCcw, MapPin } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { useStore } from "../context/StoreContext";
import { useAuth } from "../context/AuthContext";
import { PaymentStep } from "../components/checkout/PaymentStep";
import { Reassurance, FreeShippingBar } from "../components/checkout/Reassurance";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputCls = "w-full rounded-xl border border-stone-300 bg-white px-4 py-3 text-sm text-stone-900 placeholder:text-stone-400 focus:outline-none focus:border-[#C85A32] transition-colors";
const emptyAddr = { name: "", email: "", phone: "", line1: "", line2: "", postal_code: "", city: "", country: "CH", note: "" };

const IdentifyStep = ({ t, onGuest, login }) => {
  const [mode, setMode] = useState(null);
  const [form, setForm] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await login(form.email, form.password); toast.success(t.account.welcome); }
    catch (err) { const d = err.response?.data?.detail; toast.error(typeof d === "string" ? d : "Identifiants incorrects"); }
    finally { setBusy(false); }
  };
  return (
    <div className="grid sm:grid-cols-2 gap-4" data-testid="checkout-identify">
      <button data-testid="checkout-guest" onClick={onGuest} className="rounded-2xl border border-stone-300 bg-white p-6 text-left hover:border-stone-900 transition-colors">
        <UserPlus size={18} className="text-[#C85A32]" />
        <p className="mt-3 font-serif text-xl text-stone-900">{t.checkoutPage.guest}</p>
        <p className="mt-1 text-xs text-stone-500">{t.checkoutPage.guestSub}</p>
      </button>
      <div className={`rounded-2xl border bg-white p-6 transition-colors ${mode === "login" ? "border-stone-900" : "border-stone-300"}`}>
        <button data-testid="checkout-login-toggle" onClick={() => setMode("login")} className="text-left w-full">
          <User size={18} className="text-[#4A624E]" />
          <p className="mt-3 font-serif text-xl text-stone-900">{t.checkoutPage.login}</p>
          <p className="mt-1 text-xs text-stone-500">{t.checkoutPage.loginSub}</p>
        </button>
        {mode === "login" && (
          <form onSubmit={submit} className="mt-4 space-y-3">
            <input data-testid="checkout-login-email" required type="email" className={inputCls} placeholder={t.account.email} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            <input data-testid="checkout-login-password" required type="password" className={inputCls} placeholder={t.account.password} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
            <button data-testid="checkout-login-submit" disabled={busy} className="w-full rounded-full bg-stone-900 text-white py-3 text-[11px] uppercase tracking-[0.15em] hover:bg-[#C85A32] transition-colors disabled:opacity-60">{busy ? "..." : t.account.loginCta}</button>
            <p className="text-[11px] text-stone-400 text-center"><Link to="/compte" className="underline">{t.account.register}</Link></p>
          </form>
        )}
      </div>
    </div>
  );
};

const StepTitle = ({ n, done, label }) => (
  <h2 className="text-[11px] font-mono uppercase tracking-[0.3em] text-stone-500 mb-4 flex items-center gap-3">
    <span className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] ${done ? "bg-[#4A624E] text-white" : "bg-stone-900 text-white"}`}>{done ? <Check size={12} /> : n}</span> {label}
  </h2>
);

export default function CheckoutPage() {
  const { t, lang, totals, promo, products, productsLoaded, visitorId, track, config } = useStore();
  const { user, login } = useAuth();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const tc = t.checkoutPage;
  const subscribeId = params.get("subscribe");
  const subProduct = subscribeId ? products.find((p) => p.id === subscribeId) : null;
  const [guest, setGuest] = useState(false);
  const [addr, setAddr] = useState(emptyAddr);
  const [saveAddr, setSaveAddr] = useState(true);
  const [busy, setBusy] = useState(false);
  const [clientSecret, setClientSecret] = useState(null);

  useEffect(() => { window.scrollTo(0, 0); }, []);
  useEffect(() => {
    if (user) setAddr((a) => ({ ...a, name: user.name || a.name, email: user.email, phone: user.phone || a.phone, ...(user.address || {}) }));
  }, [user]);
  useEffect(() => { setClientSecret(null); }, [totals.total, promo]);

  const identified = !!user || guest;
  const items = subProduct ? [{ ...subProduct, qty: 1, price: subProduct.sub_price }] : totals.items;
  const total = subProduct ? subProduct.sub_price : totals.total;
  if (!productsLoaded) return <div className="min-h-screen pt-40 text-center text-stone-500 font-mono text-sm">…</div>;
  if (!subProduct && totals.items.length === 0) {
    return <div className="min-h-screen pt-40 text-center" data-testid="checkout-empty"><p className="font-serif text-2xl text-stone-800 italic">{tc.empty}</p><Link to="/#products" className="mt-6 inline-block text-[12px] uppercase tracking-[0.18em] border-b border-stone-400 pb-1">{t.payment.backHome}</Link></div>;
  }

  const pay = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (user && saveAddr) {
        const { name, email, note, ...address } = addr;
        await axios.put(`${API}/auth/profile`, { name, phone: addr.phone, address }, { withCredentials: true }).catch(() => {});
      }
      track("checkout_redirect");
      const body = { origin_url: window.location.origin, customer: addr, visitor_id: visitorId, lang };
      const res = subProduct
        ? await axios.post(`${API}/payments/subscribe`, { ...body, product_id: subProduct.id })
        : await axios.post(`${API}/payments/checkout`, { ...body, items: totals.items.map((i) => ({ product_id: i.id, quantity: i.qty })), promo_code: promo });
      if (res.data.client_secret) {
        setClientSecret(res.data.client_secret);
        setTimeout(() => document.getElementById("checkout-payment")?.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
      } else {
        window.location.href = res.data.checkout_url;
      }
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Erreur — vérifiez vos informations.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="pt-28 lg:pt-32 pb-24 px-6 lg:px-14 xl:px-24 max-w-[1200px] mx-auto" data-testid="checkout-page">
      <p className="text-[11px] font-mono uppercase tracking-[0.3em] text-[#C85A32] mb-3">NŪRA</p>
      <h1 className="font-serif text-4xl text-stone-900 mb-10">{tc.title}</h1>
      <div className="grid lg:grid-cols-[1fr_380px] gap-10">
        <div className="space-y-10">
          <section>
            <StepTitle n="1" done={identified} label={tc.step1} />
            {identified ? (
              <p className="text-sm text-stone-700" data-testid="checkout-identified">{user ? `${tc.loggedAs} ${user.email}` : tc.guest} {!user && <button onClick={() => { setGuest(false); setClientSecret(null); }} className="ml-2 text-[11px] uppercase tracking-wide text-[#C85A32]">{tc.change}</button>}</p>
            ) : <IdentifyStep t={t} onGuest={() => setGuest(true)} login={login} />}
          </section>

          {identified && clientSecret && (
            <section data-testid="checkout-address-recap">
              <StepTitle n="2" done label={tc.step2} />
              <div className="rounded-2xl border border-stone-200 bg-white p-5 flex items-start gap-4">
                <MapPin size={16} className="text-[#C85A32] mt-0.5 shrink-0" />
                <div className="flex-1 text-sm text-stone-700 leading-relaxed">
                  <p className="text-stone-900 font-medium">{addr.name}</p>
                  <p>{addr.line1}{addr.line2 ? `, ${addr.line2}` : ""}</p>
                  <p>{addr.postal_code} {addr.city}, {addr.country}</p>
                  <p className="text-stone-500 text-xs mt-1">{addr.email}{addr.phone ? ` · ${addr.phone}` : ""}</p>
                </div>
                <button data-testid="checkout-edit-address" onClick={() => setClientSecret(null)} className="text-[11px] uppercase tracking-wide text-[#C85A32]">{tc.edit}</button>
              </div>
            </section>
          )}

          {identified && !clientSecret && (
            <motion.form initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} onSubmit={pay} className="space-y-4" data-testid="checkout-address-form">
              <StepTitle n="2" label={tc.step2} />
              <div className="grid sm:grid-cols-2 gap-3">
                <input data-testid="checkout-name" required minLength={2} className={inputCls} placeholder={t.account.name} value={addr.name} onChange={(e) => setAddr({ ...addr, name: e.target.value })} />
                <input data-testid="checkout-email" required type="email" className={inputCls} placeholder={t.account.email} value={addr.email} disabled={!!user} onChange={(e) => setAddr({ ...addr, email: e.target.value })} />
              </div>
              <input data-testid="checkout-line1" required minLength={3} className={inputCls} placeholder={tc.line1} value={addr.line1} onChange={(e) => setAddr({ ...addr, line1: e.target.value })} />
              <input data-testid="checkout-line2" className={inputCls} placeholder={tc.line2} value={addr.line2} onChange={(e) => setAddr({ ...addr, line2: e.target.value })} />
              <div className="grid grid-cols-[120px_1fr_100px] gap-3">
                <input data-testid="checkout-zip" required minLength={4} className={inputCls} placeholder={tc.zip} value={addr.postal_code} onChange={(e) => setAddr({ ...addr, postal_code: e.target.value })} />
                <input data-testid="checkout-city" required minLength={2} className={inputCls} placeholder={tc.city} value={addr.city} onChange={(e) => setAddr({ ...addr, city: e.target.value })} />
                <select data-testid="checkout-country" className={inputCls} value={addr.country} onChange={(e) => setAddr({ ...addr, country: e.target.value })}>{(config.shipping_countries || ["CH", "LI"]).map((c) => <option key={c} value={c}>{c}</option>)}</select>
              </div>
              <input data-testid="checkout-phone" className={inputCls} placeholder={tc.phone} value={addr.phone} onChange={(e) => setAddr({ ...addr, phone: e.target.value })} />
              <textarea data-testid="checkout-note" rows={2} className={inputCls} placeholder={tc.note} value={addr.note} onChange={(e) => setAddr({ ...addr, note: e.target.value })} />
              {user && <label className="flex items-center gap-2 text-sm text-stone-600"><input type="checkbox" checked={saveAddr} onChange={(e) => setSaveAddr(e.target.checked)} className="accent-[#C85A32] w-4 h-4" /> {tc.saveAddress}</label>}
              <p className="text-[11px] text-stone-400">{tc.chOnly} {config.delivery_delay?.[lang] ? `· ${config.delivery_delay[lang]}` : ""}</p>
              <button data-testid="checkout-pay" type="submit" disabled={busy} className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-full bg-stone-900 text-[#FAF7F2] px-10 py-4 text-[12px] uppercase tracking-[0.2em] hover:bg-[#C85A32] transition-colors disabled:opacity-60">
                <Lock size={13} /> {busy ? "..." : `${tc.pay} — ${Number(total).toFixed(2)} CHF`}
              </button>
              <p className="text-[11px] text-stone-500 leading-relaxed">{tc.promoHint}</p>
            </motion.form>
          )}

          {identified && clientSecret && (
            <motion.section id="checkout-payment" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="scroll-mt-32">
              <StepTitle n="3" label={tc.step3} />
              <PaymentStep clientSecret={clientSecret} tc={tc} />
            </motion.section>
          )}
        </div>

        <aside className="lg:sticky lg:top-28 self-start rounded-2xl border border-stone-200 bg-white p-6" data-testid="checkout-summary">
          <p className="text-[11px] font-mono uppercase tracking-[0.3em] text-stone-500 mb-4">{tc.summary}</p>
          <ul className="divide-y divide-stone-100">
            {items.map((i) => (
              <li key={i.id} className="py-3 flex items-center gap-3">
                <div className="w-14 h-16 rounded-lg overflow-hidden shrink-0" style={{ backgroundColor: i.bgTint }}><img src={i.image} alt="" className="w-full h-full object-cover" /></div>
                <div className="flex-1 min-w-0"><p className="text-sm text-stone-900 truncate">{i.name[lang]}</p>{i.subtitle?.[lang] && <p className="text-[11px] text-stone-500 truncate">{i.subtitle[lang]}</p>}<p className="text-[10px] font-mono text-stone-500 mt-0.5">{subProduct ? <span className="inline-flex items-center gap-1"><RefreshCcw size={10} /> {tc.subscription}</span> : `× ${i.qty}`}</p></div>
                <span className="font-mono text-sm">{(i.price * i.qty).toFixed(2)}</span>
              </li>
            ))}
          </ul>
          {!subProduct && (
            <div className="mt-4 pt-4 border-t border-stone-200 space-y-1.5 text-sm">
              <div className="flex justify-between text-stone-600"><span>{t.cartDrawer.subtotal}</span><span className="font-mono">{totals.subtotal.toFixed(2)}</span></div>
              {totals.discount > 0 && <div className="flex justify-between text-[#4A624E]"><span>{t.cartDrawer.discount} ({promo})</span><span className="font-mono">−{totals.discount.toFixed(2)}</span></div>}
              <div className="flex justify-between text-stone-600"><span>{t.cartDrawer.shipping}</span><span className="font-mono">{totals.shipping === 0 ? t.cartDrawer.free : totals.shipping.toFixed(2)}</span></div>
            </div>
          )}
          <div className="mt-3 pt-3 border-t border-stone-900 flex justify-between font-mono text-base"><span>{t.cartDrawer.total}</span><span data-testid="checkout-total">{Number(total).toFixed(2)} CHF</span></div>
          {subProduct && <p className="mt-2 text-[11px] text-stone-500">{t.sub.note}</p>}
          {!subProduct && <FreeShippingBar subtotal={totals.subtotal - totals.discount} threshold={config.free_shipping_threshold} reached={t.cartDrawer.freeShippingReached} label={t.cartDrawer.freeShippingLeft(Math.max(0, config.free_shipping_threshold - (totals.subtotal - totals.discount)).toFixed(2))} />}
          <Reassurance items={tc.reassurance} />
        </aside>
      </div>
    </div>
  );
}
